import type jsPDF from 'jspdf';
import type { EnterpriseReportModel } from './enterpriseReportModel';
import { formatMinutes, formatPriorityLines, presentActionStatus, stageTableRows } from './enterpriseReportModel';
import { MAP_DRAW, physicalPageLabels, planMapLayout } from './enterpriseReportLayout';

const BLUE: [number, number, number] = [30, 64, 110];
const INK: [number, number, number] = [30, 41, 59];
const MUTED: [number, number, number] = [100, 116, 139];
const LINE: [number, number, number] = [226, 232, 240];
const RED: [number, number, number] = [185, 28, 28];

async function cropMapSlice(
  dataUrl: string,
  frame: { x: number; y: number; width: number; height: number },
  fullWidth: number,
  fullHeight: number,
): Promise<string> {
  const image = await loadImage(dataUrl);
  const canvas = document.createElement('canvas');
  const sx = (frame.x / fullWidth) * image.width;
  const sy = (frame.y / fullHeight) * image.height;
  const sw = (frame.width / fullWidth) * image.width;
  const sh = (frame.height / fullHeight) * image.height;
  canvas.width = Math.max(1, Math.round(sw));
  canvas.height = Math.max(1, Math.round(sh));
  const context = canvas.getContext('2d');
  if (!context) return dataUrl;
  context.drawImage(image, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/png');
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Falha ao ler o mapa exportado.'));
    image.src = dataUrl;
  });
}
const AMBER: [number, number, number] = [180, 83, 9];

interface DrawContext {
  pdf: jsPDF;
  margin: number;
  pageNumber: number;
  processName: string;
}

function contentWidth(ctx: DrawContext): number {
  return ctx.pdf.internal.pageSize.getWidth() - ctx.margin * 2;
}

function addChrome(ctx: DrawContext, title: string) {
  const { pdf, margin } = ctx;
  const width = pdf.internal.pageSize.getWidth();
  const height = pdf.internal.pageSize.getHeight();
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(...MUTED);
  pdf.text(ctx.processName, margin, 10);
  pdf.text(title, width - margin, 10, { align: 'right' });
  pdf.setDrawColor(...LINE);
  pdf.setLineWidth(0.2);
  pdf.line(margin, 13, width - margin, 13);
  pdf.line(margin, height - 12, width - margin, height - 12);
  pdf.text('Documento confidencial · Análise sujeita a validação em Gemba', width - margin, height - 7, { align: 'right' });
}

function stampPageNumbers(pdf: jsPDF) {
  const labels = physicalPageLabels(pdf.getNumberOfPages());
  labels.forEach((label, index) => {
    pdf.setPage(index + 1);
    const height = pdf.internal.pageSize.getHeight();
    pdf.setFillColor(255, 255, 255);
    pdf.rect(12, height - 11, 28, 7, 'F');
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(...MUTED);
    pdf.text(label, 14, height - 7);
  });
}

function ensureSpace(ctx: DrawContext, y: number, needed: number, title: string): number {
  const limit = ctx.pdf.internal.pageSize.getHeight() - 18;
  if (y + needed <= limit) return y;
  ctx.pdf.addPage();
  ctx.pageNumber += 1;
  addChrome(ctx, title);
  return 20;
}

function sectionTitle(ctx: DrawContext, y: number, text: string): number {
  ctx.pdf.setFont('helvetica', 'bold');
  ctx.pdf.setFontSize(12);
  ctx.pdf.setTextColor(...BLUE);
  ctx.pdf.text(text, ctx.margin, y);
  return y + 7;
}

export function pdfSafe(value: string): string {
  return value
    .replace(/↓/g, '-')
    .replace(/↑/g, '+')
    .replace(/[−–—]/g, '-')
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'");
}

function paragraph(ctx: DrawContext, y: number, text: string, title: string): number {
  const width = contentWidth(ctx);
  text = pdfSafe(text);
  ctx.pdf.setFont('helvetica', 'normal');
  ctx.pdf.setFontSize(9);
  ctx.pdf.setTextColor(...INK);
  const lines = ctx.pdf.splitTextToSize(text, width);
  y = ensureSpace(ctx, y, lines.length * 4.2 + 2, title);
  ctx.pdf.text(lines, ctx.margin, y);
  return y + lines.length * 4.2 + 2;
}

function drawShareBar(ctx: DrawContext, y: number, label: string, percent: number | null): number {
  const width = contentWidth(ctx);
  ctx.pdf.setFont('helvetica', 'normal');
  ctx.pdf.setFontSize(8);
  ctx.pdf.setTextColor(...MUTED);
  ctx.pdf.text(label, ctx.margin, y);
  const barX = ctx.margin + 32;
  const barW = width - 48;
  ctx.pdf.setFillColor(241, 245, 249);
  ctx.pdf.rect(barX, y - 3, barW, 3.2, 'F');
  if (percent != null) {
    ctx.pdf.setFillColor(...BLUE);
    ctx.pdf.rect(barX, y - 3, Math.max(0, Math.min(100, percent)) / 100 * barW, 3.2, 'F');
    ctx.pdf.setTextColor(...INK);
    ctx.pdf.text(`${percent.toFixed(0)}%`, barX + barW + 2, y);
  }
  return y + 7;
}

async function trimMapCanvas(dataUrl: string, widthPx: number, heightPx: number): Promise<{ dataUrl: string; widthPx: number; heightPx: number }> {
  const image = await loadImage(dataUrl);
  const source = document.createElement('canvas');
  source.width = image.width;
  source.height = image.height;
  const context = source.getContext('2d');
  if (!context || image.width < 2 || image.height < 2) return { dataUrl, widthPx, heightPx };
  context.drawImage(image, 0, 0);
  const pixels = context.getImageData(0, 0, source.width, source.height).data;
  let minX = source.width;
  let minY = source.height;
  let maxX = 0;
  let maxY = 0;
  const step = Math.max(1, Math.floor(Math.min(source.width, source.height) / 500));
  for (let y = 0; y < source.height; y += step) {
    for (let x = 0; x < source.width; x += step) {
      const offset = (y * source.width + x) * 4;
      if (pixels[offset + 3] < 16) continue;
      if (pixels[offset] > 248 && pixels[offset + 1] > 248 && pixels[offset + 2] > 248) continue;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }
  if (maxX <= minX || maxY <= minY) return { dataUrl, widthPx, heightPx };
  const pad = step * 3;
  const sx = Math.max(0, minX - pad);
  const sy = Math.max(0, minY - pad);
  const sw = Math.min(source.width - sx, maxX - sx + pad);
  const sh = Math.min(source.height - sy, maxY - sy + pad);
  if (sw > source.width * 0.98 && sh > source.height * 0.98) return { dataUrl, widthPx, heightPx };
  const cropped = document.createElement('canvas');
  cropped.width = Math.max(1, Math.round(sw));
  cropped.height = Math.max(1, Math.round(sh));
  cropped.getContext('2d')?.drawImage(source, sx, sy, sw, sh, 0, 0, cropped.width, cropped.height);
  return {
    dataUrl: cropped.toDataURL('image/png'),
    widthPx: widthPx * (sw / source.width),
    heightPx: heightPx * (sh / source.height),
  };
}

export async function appendLandscapeMap(
  pdf: jsPDF,
  ctx: DrawContext,
  map: { dataUrl: string; widthPx: number; heightPx: number },
  options: { title: string; caption: string },
): Promise<void> {
  const trimmed = await trimMapCanvas(map.dataUrl, map.widthPx, map.heightPx);
  const layout = planMapLayout(trimmed.widthPx, trimmed.heightPx);
  const mapImage = { ...map, dataUrl: trimmed.dataUrl, widthPx: trimmed.widthPx, heightPx: trimmed.heightPx };
  for (let index = 0; index < layout.frames.length; index += 1) {
    const frame = layout.frames[index];
    const slice = layout.split ? await cropMapSlice(mapImage.dataUrl, frame, mapImage.widthPx, mapImage.heightPx) : mapImage.dataUrl;
    pdf.addPage('a4', 'landscape');
    ctx.pageNumber += 1;
    addChrome(ctx, options.title);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    pdf.setTextColor(...BLUE);
    const title = layout.split
      ? `${options.title}  ·  parte ${index + 1} de ${layout.frames.length}`
      : options.title;
    pdf.text(title, ctx.margin, 20);
    const frameScale = Math.min(MAP_DRAW.availW / frame.width, MAP_DRAW.availH / frame.height);
    pdf.addImage(slice, 'PNG', MAP_DRAW.originX, MAP_DRAW.originY, frame.width * frameScale, frame.height * frameScale, undefined, 'FAST');
    pdf.setFontSize(7);
    pdf.setTextColor(...MUTED);
    pdf.text(options.caption, 14, pdf.internal.pageSize.getHeight() - 16);
    if (frame.continued) pdf.text('Continua na página seguinte.', pdf.internal.pageSize.getWidth() - 14, pdf.internal.pageSize.getHeight() - 16, { align: 'right' });
  }
}

export async function renderEnterprisePdf(model: EnterpriseReportModel, map?: { dataUrl: string; widthPx: number; heightPx: number } | null, options?: { write?: (bytes: ArrayBuffer) => void }): Promise<void> {
  const { jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const ctx: DrawContext = { pdf, margin: 14, pageNumber: 1, processName: model.processName };
  addChrome(ctx, 'Relatório Enterprise');

  let y = 20;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.setTextColor(...BLUE);
  pdf.text('WORKFLOW', ctx.margin, y);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(...MUTED);
  pdf.text('Lean Process Intelligence', ctx.margin + 28, y);
  y += 8;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(16);
  pdf.setTextColor(...INK);
  const nameLines = pdf.splitTextToSize(model.processName, contentWidth(ctx));
  pdf.text(nameLines, ctx.margin, y);
  y += nameLines.length * 6 + 2;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(...MUTED);
  pdf.text('Relatório Enterprise · Atual, com proposta e estado-alvo quando existirem', ctx.margin, y);
  y += 5;
  const meta = [
    model.generatedAt,
    model.organizationName ?? 'Organização não informada nesta exportação',
    model.version,
  ].join('   ·   ');
  pdf.text(meta, ctx.margin, y);
  y += 8;

  y = sectionTitle(ctx, y, 'Indicadores');
  const kpis = model.kpis;
  if (kpis.length === 0) {
    y = paragraph(ctx, y, 'Não há indicadores numéricos sustentados pelos dados deste mapa.', 'Resumo');
  } else {
    const gap = 3;
    const columns = Math.min(4, kpis.length);
    const cardW = (contentWidth(ctx) - gap * (columns - 1)) / columns;
    kpis.forEach((kpi, index) => {
      const col = index % columns;
      const row = Math.floor(index / columns);
      const x = ctx.margin + col * (cardW + gap);
      const cardY = y + row * 16;
      pdf.setDrawColor(...LINE);
      pdf.setLineWidth(0.2);
      pdf.rect(x, cardY, cardW, 14);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(7);
      pdf.setTextColor(...MUTED);
      pdf.text(kpi.label.toUpperCase(), x + 2, cardY + 5);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(...INK);
      const value = pdf.splitTextToSize(kpi.value, cardW - 4)[0] ?? '';
      pdf.text(value, x + 2, cardY + 10);
    });
    y += Math.ceil(kpis.length / columns) * 16 + 4;
  }

  const probabilityNote = model.unavailableNotes.find((note) => note.startsWith('Probabilidade insuficiente'));
  if (probabilityNote) y = paragraph(ctx, y, probabilityNote, 'Resumo');

  y = sectionTitle(ctx, y, 'Leitura executiva');
  y = paragraph(ctx, y, model.executiveReading.join(' '), 'Resumo');
  pdf.setFont('helvetica', 'italic');
  pdf.setFontSize(8);
  pdf.setTextColor(...MUTED);
  y = paragraph(ctx, y, 'Síntese do mapa. A leitura não atribui causa.', 'Resumo');

  if (model.priorities.length > 0) {
    y = sectionTitle(ctx, y, 'Prioridades do fluxo');
    for (const priority of model.priorities) {
      const lines = formatPriorityLines(priority).map((line) => pdfSafe(line));
      y = ensureSpace(ctx, y, 16, 'Resumo');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(...BLUE);
      pdf.text(lines[0] ?? '', ctx.margin, y);
      y += 4.5;
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...INK);
      const criterion = pdf.splitTextToSize(lines[1] ?? '', contentWidth(ctx));
      pdf.text(criterion, ctx.margin, y);
      y += criterion.length * 4.2;
      pdf.setFont('helvetica', 'normal');
      const detail = pdf.splitTextToSize(lines[2] ?? '', contentWidth(ctx));
      pdf.text(detail, ctx.margin, y);
      y += detail.length * 4.2 + 2;
    }
  }

  if (model.pageOneRisks.length > 0) {
    y = sectionTitle(ctx, y, 'Riscos relevantes');
    const riskCount = model.pageOneRisks.length;
    y = paragraph(ctx, y, riskCount === 1 ? '1 exceção de alto impacto' : `${riskCount} exceções de alto impacto`, 'Resumo');
    for (const item of model.pageOneRisks) {
      y = paragraph(ctx, y, `Risco excepcional: ${item.name} alcança ${item.occurrenceLabel} dos pedidos. Nessa rota, a permanência é ${item.permanenceLabel}.`, 'Resumo');
    }
  }

  if (map && map.widthPx > 0 && map.heightPx > 0) {
    await appendLandscapeMap(pdf, ctx, map, {
      title: 'Mapa do estado atual',
      caption: 'Permanência = tempo da etapa quando ela ocorre    Realização = tempo de trabalho    P = pessoas    losango = decisão',
    });
    pdf.addPage('a4', 'portrait');
    ctx.pageNumber += 1;
    addChrome(ctx, 'Análise do fluxo');
    y = 20;
  }

  y = sectionTitle(ctx, y, 'Rota principal');
  if (model.composition.totalLeadMinutes == null) {
    y = paragraph(ctx, y, 'Lead time da rota principal não calculável com os dados disponíveis.', 'Análise');
  } else {
    const expected = model.composition.expectedLeadMinutes == null
      ? 'Lead time esperado não calculado.'
      : `Lead time esperado: ${formatMinutes(model.composition.expectedLeadMinutes)}.`;
    y = paragraph(ctx, y, `Lead time da rota principal: ${formatMinutes(model.composition.totalLeadMinutes)}. Realização: ${formatMinutes(model.composition.totalTouchMinutes) ?? 'não informada em todas as etapas'}. Espera: ${formatMinutes(model.composition.totalWaitMinutes) ?? 'não calculável'}. ${expected}`, 'Análise');
    y = drawShareBar(ctx, y, 'Realização', model.composition.processingShare);
    y = drawShareBar(ctx, y, 'Espera', model.composition.waitShare);
    if (model.composition.pcePercent != null) {
      y = paragraph(ctx, y, `PCE da rota principal: ${model.composition.pcePercent.toFixed(1).replace('.', ',')}%. Rotas condicionais foram tratadas à parte.`, 'Análise');
    }
  }

  if (model.rankings.mainPermanences.length > 0 || model.rankings.mainWaits.length > 0) {
    y = sectionTitle(ctx, y, 'Maiores permanências e esperas da rota principal');
    for (const item of model.rankings.mainPermanences.slice(0, 5)) {
      y = paragraph(ctx, y, `Permanência · ${item.name}: ${item.label}`, 'Análise');
    }
    for (const item of model.rankings.mainWaits.slice(0, 5)) {
      y = paragraph(ctx, y, `Espera · ${item.name}: ${item.label}`, 'Análise');
    }
  }

  if (model.exceptions.length > 0) {
    y = sectionTitle(ctx, y, 'Exceções e riscos do fluxo');
    for (const item of model.exceptions) {
      y = paragraph(ctx, y, `${item.name}. Probabilidade de passagem ${item.occurrenceLabel}. Quando a rota ocorre, a permanência é ${item.permanenceLabel}. Classificação: ${item.impactLabel}. ${item.message}`, 'Exceções');
    }
  }

  if (model.stages.length > 0) {
    y = sectionTitle(ctx, y, 'Etapas do fluxo');
    autoTable(pdf, {
      head: [['#', 'Etapa', 'Responsável', 'Probabilidade de passagem', 'Permanência', 'Realização', 'Espera', 'Participação', 'Sinais']],
      body: stageTableRows(model).map((row) => row.map((cell) => pdfSafe(cell))),
      startY: y,
      margin: { left: ctx.margin, right: ctx.margin, bottom: 16 },
      showHead: 'everyPage',
      styles: { fontSize: 7, cellPadding: 1.3, overflow: 'linebreak', valign: 'middle', textColor: INK, lineColor: LINE, lineWidth: 0.1 },
      headStyles: { fillColor: BLUE, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 6.5 },
      columnStyles: {
        0: { cellWidth: 8, halign: 'center' },
        1: { cellWidth: 32 },
        2: { cellWidth: 26 },
        3: { cellWidth: 22, halign: 'right' },
        4: { cellWidth: 18, halign: 'right' },
        5: { cellWidth: 16, halign: 'right' },
        6: { cellWidth: 16, halign: 'right' },
        7: { cellWidth: 22 },
        8: { cellWidth: 22 },
      },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 8) {
          const value = String(data.cell.raw ?? '');
          if (value.includes('ALTO IMPACTO') || value.includes('TEMPO ELEVADO')) data.cell.styles.textColor = RED;
          else if (value.includes('ESPERA ELEVADA') || value.includes('EXCEÇÃO RARA')) data.cell.styles.textColor = AMBER;
        }
      },
      didDrawPage: (data) => {
        if (data.pageNumber > 1) addChrome(ctx, 'Análise do fluxo');
      },
    });
    y = ((pdf as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? y) + 8;
  }

  y = ensureSpace(ctx, y, 40, 'Diagnóstico');
  y = sectionTitle(ctx, y, 'Diagnóstico do fluxo');
  const flowBits = [
    `${model.flow.activities} atividades`,
    `${model.flow.decisions} decisões`,
    model.flow.returns == null ? null : `${model.flow.returns} retornos`,
    model.flow.handoffs == null ? null : `${model.flow.handoffs} handoffs`,
    model.flow.owners.length > 0 ? `Responsáveis: ${model.flow.owners.join(' · ')}` : null,
    model.flow.systemCount == null ? null : `${model.flow.systemCount} etapas com sistema informado`,
  ].filter(Boolean);
  y = paragraph(ctx, y, flowBits.join(' · ') + '.', 'Diagnóstico');
  y = paragraph(ctx, y, model.bottleneck.evidence, 'Diagnóstico');

  if (model.evolution.length > 0) {
    y = ensureSpace(ctx, y, 36, 'Evolução');
    y = sectionTitle(ctx, y, 'Evolução do fluxo');
    const kaizenNote = model.kaizenUnchanged
      ? ' Cenário Kaizen sem alteração mensurável nos indicadores calculados.'
      : '';
    y = paragraph(ctx, y, `Atual é a condição mapeada. Cenário Kaizen é proposta. Estado futuro é o desenho-alvo. A variação é projetada.${kaizenNote}`, 'Evolução');
    autoTable(pdf, {
      head: [['Indicador', 'Atual', 'Cenário Kaizen', 'Estado futuro', 'Variação projetada']],
      body: model.evolution.map((row) => [
        pdfSafe(row.indicator),
        pdfSafe(row.current),
        pdfSafe(row.kaizenNote ? `${row.kaizen} · ${row.kaizenNote}` : row.kaizen),
        pdfSafe(row.future),
        pdfSafe(row.variation),
      ]),
      startY: y,
      margin: { left: ctx.margin, right: ctx.margin, bottom: 16 },
      showHead: 'everyPage',
      styles: { fontSize: 8, cellPadding: 1.6, textColor: INK },
      headStyles: { fillColor: BLUE, textColor: 255 },
      didDrawPage: (data) => {
        if (data.pageNumber > 1) addChrome(ctx, 'Evolução');
      },
    });
    y = ((pdf as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? y) + 8;
  }

  if (model.structuralChanges.length > 0) {
    y = sectionTitle(ctx, y, 'Mudanças de estrutura');
    for (const change of model.structuralChanges) {
      y = paragraph(ctx, y, change, 'Estrutura');
    }
  }

  if (model.expectedBreakdown.length > 0) {
    y = ensureSpace(ctx, y, 28, 'Lead time esperado');
    y = sectionTitle(ctx, y, 'Composição do lead time esperado');
    y = paragraph(ctx, y, 'Cada contribuição é a permanência multiplicada pela probabilidade de passagem. A etapa entra uma vez.', 'Lead time esperado');
    autoTable(pdf, {
      head: [['Etapa', 'Probabilidade de passagem', 'Permanência', 'Contribuição']],
      body: [
        ...model.expectedBreakdown.map((row) => [row.name, row.probability, row.permanence, row.contribution]),
        ['Total esperado', '', '', formatMinutes(model.composition.expectedLeadMinutes) ?? '-'],
      ],
      startY: y,
      margin: { left: ctx.margin, right: ctx.margin, bottom: 16 },
      showHead: 'everyPage',
      styles: { fontSize: 8, cellPadding: 1.4, textColor: INK },
      headStyles: { fillColor: BLUE, textColor: 255 },
      didDrawPage: (data) => {
        if (data.pageNumber > 1) addChrome(ctx, 'Lead time esperado');
      },
    });
    y = ((pdf as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? y) + 8;
  }

  if (model.executiveConclusion.length > 0) {
    y = ensureSpace(ctx, y, 28, 'Conclusão');
    y = sectionTitle(ctx, y, 'Conclusão executiva');
    for (const line of model.executiveConclusion) {
      y = paragraph(ctx, y, line, 'Conclusão');
    }
  }

  if (model.opportunities.length > 0) {
    y = ensureSpace(ctx, y, 28, 'Oportunidades');
    y = sectionTitle(ctx, y, 'Oportunidades de investigação');
    for (const item of model.opportunities) {
      const evidence = item.evidence.replace(/[.\s]+$/, '');
      y = paragraph(ctx, y, `${item.priority} ${item.opportunity}. Evidência: ${evidence}. ${item.recommendation}`, 'Oportunidades');
    }
  }

  if (model.actions.length > 0) {
    y = ensureSpace(ctx, y, 28, 'Plano');
    y = sectionTitle(ctx, y, 'Plano de melhoria');
    const rows = model.actions.map((action) => [
      pdfSafe(action.title || action.description || 'Ação'),
      pdfSafe(action.responsible || action.responsavel || '-'),
      pdfSafe(action.deadline || action.prazo || '-'),
      pdfSafe(presentActionStatus(action.status)),
      pdfSafe(model.actionOrigins.find((item) => item.id === action.id)?.origin || '-'),
    ]);
    autoTable(pdf, {
      head: [['Ação / contramedida', 'Responsável', 'Prazo', 'Status', 'Origem']],
      body: rows,
      startY: y,
      margin: { left: ctx.margin, right: ctx.margin, bottom: 16 },
      showHead: 'everyPage',
      styles: { fontSize: 8, cellPadding: 1.6, textColor: INK },
      headStyles: { fillColor: BLUE, textColor: 255 },
      didDrawPage: (data) => {
        if (data.pageNumber > 1) addChrome(ctx, 'Plano');
      },
    });
    y = ((pdf as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? y) + 8;
  }

  if (model.problems.length > 0) {
    y = ensureSpace(ctx, y, 20, 'Problemas');
    y = sectionTitle(ctx, y, 'Problemas registrados');
    for (const problem of model.problems) {
      y = paragraph(ctx, y, problem.title, 'Problemas');
    }
  }

  if (model.kaizens.length > 0) {
    y = ensureSpace(ctx, y, 24, 'Kaizens');
    y = sectionTitle(ctx, y, 'Kaizens associados');
    for (const kaizen of model.kaizens) {
      y = paragraph(ctx, y, `${kaizen.title}. Status: ${kaizen.status ?? 'não informado'}. Responsável: ${kaizen.responsible ?? 'não informado'}.`, 'Kaizens');
      for (const metric of kaizen.metrics ?? []) {
        const part = (value: number | null) => value == null ? 'não informado' : String(value);
        y = paragraph(ctx, y, `${metric.label}: ${part(metric.baseline)} → ${part(metric.target)} → ${part(metric.current)}${metric.unit ? ` ${metric.unit}` : ''}`, 'Kaizens');
      }
    }
  }

  if (model.financial.annualCostSaved != null) {
    y = ensureSpace(ctx, y, 16, 'Financeiro');
    y = sectionTitle(ctx, y, 'Impacto anual estimado');
    y = paragraph(ctx, y, `${model.financial.annualCostSaved.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} por ano. Valor informado. Não é caixa realizado nem estimativa calculada a partir do lead time.`, 'Financeiro');
  }

  y = ensureSpace(ctx, y, 36, 'Metodologia');
  y = sectionTitle(ctx, y, 'Sobre esta análise');
  y = paragraph(ctx, y, `Processo: ${model.processName}. Organização: ${model.organizationName ?? 'não informada'}. Data: ${model.generatedAt}. Versão: ${model.version}. Etapas analisadas: ${model.flow.activities}. Fonte: mapa DATAFLOW / WORKFLOW.`, 'Metodologia');
  for (const rule of model.methodology) {
    y = paragraph(ctx, y, rule, 'Metodologia');
  }

  stampPageNumbers(pdf);
  if (options?.write) {
    options.write(pdf.output('arraybuffer'));
    return;
  }
  const fileName = `${model.processName.replace(/[^a-z0-9]/gi, '_')}_Enterprise_Report.pdf`;
  pdf.save(fileName);
}
